import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { parseLocalDate } from '../../util/helper';
import './Scheduler.css';

const AppointmentScheduler = ({
  startDate = new Date(),
  daysCount = 7,
  timeSlots = ['8-12', '9-12', '11-2', '12-5'],
  unavailable = [],
  showNavigation = true,
  showLegend = true,
  onSlotSelected = null,
  text = {},
  maxTotalDays = null,
  maxDate = null,
  initialSelectedSlot = null
}) => {
  const defaultText = {
    headerTitle: 'SELECT BY CLICKING BELOW',
    prevWeekButton: '◀ PREV WEEK',
    nextWeekButton: 'NEXT WEEK ▶',
    arrivalWindowsTitle: 'ARRIVAL WINDOWS',
    gridLabel: 'Choose your appointment time',
    availableTimeLabel: 'Available Time',
    notAvailableTimeLabel: 'Not Available Time',
    selectedTimeLabel: 'Selected Time',
    selectedAppointmentLabel: 'Selected Appointment:',
    dayNames: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
    dateOptions: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    },
    ...text
  };

  const [currentStartDate, setCurrentStartDate] = useState(new Date(startDate));
  const [originalStartDate] = useState(new Date(startDate));
  const [selectedSlot, setSelectedSlot] = useState(initialSelectedSlot);

  // Update selectedSlot when initialSelectedSlot prop changes
  // Also navigate to the week containing the selected date
  useEffect(() => {
    // An unparseable date is treated as no selection.
    const selectedDate = !initialSelectedSlot
      ? null
      : initialSelectedSlot.date instanceof Date
        ? initialSelectedSlot.date
        : parseLocalDate(initialSelectedSlot.dateString || initialSelectedSlot.date);

    if (selectedDate) {
      // Create the slot with proper Date object
      const normalizedSlot = {
        timeSlot: initialSelectedSlot.timeSlot,
        date: selectedDate
      };

      setSelectedSlot(normalizedSlot);

      // Calculate how many days from the original start date
      const daysDiff = Math.floor((selectedDate - originalStartDate) / (1000 * 60 * 60 * 24));

      // Calculate which week (round down to nearest week start)
      const weekNumber = Math.floor(daysDiff / daysCount);
      const weekStartOffset = weekNumber * daysCount;

      // Set the current start date to show the correct week
      const weekStart = new Date(originalStartDate);
      weekStart.setDate(weekStart.getDate() + weekStartOffset);
      setCurrentStartDate(weekStart);
    } else {
      // Reset selection and date when there's no initial slot
      setSelectedSlot(null);
      const newDate = new Date(startDate);
      setCurrentStartDate(newDate);
    }
  }, [initialSelectedSlot, originalStartDate, daysCount, startDate]);

  const formatDate = useCallback((date) => {
    return date.toISOString().split('T')[0];
  }, []);

  const getDatesArray = useCallback(() => {
    const dates = [];
    const current = new Date(currentStartDate);

    for (let i = 0; i < daysCount; i++) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }, [currentStartDate, daysCount]);

  const isDateUnavailable = useCallback((date) => {
    const dateString = formatDate(date);
    return unavailable.some(item => item === dateString);
  }, [unavailable, formatDate]);

  // Dates beyond the API's data window have no availability data. Absence from
  // the `unavailable` list must NOT be treated as available for these dates,
  // otherwise every out-of-window date renders as selectable.
  const isDateOutOfRange = useCallback((date) => {
    if (!maxDate) return false;
    return formatDate(date) > formatDate(maxDate);
  }, [maxDate, formatDate]);

  const isSlotUnavailable = useCallback((timeSlot, date) => {
    const dateString = formatDate(date);
    const slotKey = `${dateString}:${timeSlot}`;
    return unavailable.includes(slotKey);
  }, [unavailable, formatDate]);

  const isSlotAvailable = useCallback((timeSlot, date) => {
    return !isDateOutOfRange(date) && !isDateUnavailable(date) && !isSlotUnavailable(timeSlot, date);
  }, [isDateOutOfRange, isDateUnavailable, isSlotUnavailable]);

  const handlePrevWeek = useCallback(() => {
    const newDate = new Date(currentStartDate);
    newDate.setDate(newDate.getDate() - daysCount);

    if (newDate >= originalStartDate) {
      setCurrentStartDate(newDate);
    }
  }, [currentStartDate, daysCount, originalStartDate]);

  const handleNextWeek = useCallback(() => {
    const newDate = new Date(currentStartDate);
    newDate.setDate(newDate.getDate() + daysCount);
    setCurrentStartDate(newDate);
  }, [currentStartDate, daysCount]);

  const handleSlotClick = useCallback((timeSlot, date) => {
    if (!isSlotAvailable(timeSlot, date)) return;

    const newSelection = { timeSlot, date: new Date(date) };
    setSelectedSlot(newSelection);

    if (onSlotSelected) {
      onSlotSelected({
        timeSlot: newSelection.timeSlot,
        date: newSelection.date,
        dateString: formatDate(newSelection.date)
      });
    }
  }, [isSlotAvailable, onSlotSelected, formatDate]);

  const dates = getDatesArray();
  const isPrevDisabled = currentStartDate <= originalStartDate;

  // Calculate if next button should be disabled. Prefer the absolute maxDate
  // (the last date the API returned data for) so navigation can't page past the
  // data window; fall back to the relative maxTotalDays when no maxDate is given.
  const isNextDisabled = (() => {
    const nextWeekStart = new Date(currentStartDate);
    nextWeekStart.setDate(nextWeekStart.getDate() + daysCount);

    if (maxDate) {
      return formatDate(nextWeekStart) > formatDate(maxDate);
    }

    if (maxTotalDays !== null) {
      const daysSinceStart = Math.floor((nextWeekStart - originalStartDate) / (1000 * 60 * 60 * 24));
      return daysSinceStart >= maxTotalDays;
    }

    return false;
  })();

  const gridStyle = {
    gridTemplateColumns: `120px repeat(${daysCount}, 1fr)`
  };

  const slotLabel = (timeSlot, date, isAvailable) => {
    const when = date.toLocaleDateString('en-US', defaultText.dateOptions);
    return isAvailable
      ? `${timeSlot}, ${when}`
      : `${timeSlot}, ${when}, ${defaultText.notAvailableTimeLabel}`;
  };

  return (
    <div class="appointment-scheduler-widget-container">
      <div class="scheduler-widget-main">
        {showNavigation && (
          <div class="scheduler-widget-header">
            <button
              class="scheduler-widget-nav-button"
              onClick={handlePrevWeek}
              disabled={isPrevDisabled}
            >
              {defaultText.prevWeekButton}
            </button>
            <div class="scheduler-widget-week-title">
              {defaultText.headerTitle}
            </div>
            <button
              class="scheduler-widget-nav-button"
              onClick={handleNextWeek}
              disabled={isNextDisabled}
            >
              {defaultText.nextWeekButton}
            </button>
          </div>
        )}

        <div
          class="scheduler-widget-grid"
          style={gridStyle}
          role="group"
          aria-label={defaultText.gridLabel}
        >
          {/* Header row */}
          <div class="scheduler-widget-arrival-header">
            {defaultText.arrivalWindowsTitle}
          </div>

          {/* Date headers */}
          {dates.map((date, index) => {
            const monthDay = `${date.getMonth() + 1}/${date.getDate()}`;
            const dayName = defaultText.dayNames[date.getDay()];

            return (
              <div key={index} class="scheduler-widget-day-header">
                <div class="scheduler-widget-date">{monthDay}</div>
                <div class="scheduler-widget-day">{dayName}</div>
              </div>
            );
          })}

          {/* Time slot rows */}
          {timeSlots.map((timeSlot) => [
            // Time slot label
            <div key={`${timeSlot}-label`} class="scheduler-widget-time-label">
              {timeSlot}
            </div>,

            // Time slots for each date
            ...dates.map((date) => {
              const isAvailable = isSlotAvailable(timeSlot, date);
              const isSelected = selectedSlot &&
                selectedSlot.timeSlot === timeSlot &&
                formatDate(selectedSlot.date) === formatDate(date);

              let className = 'scheduler-widget-time-slot ';
              if (isSelected) {
                className += 'scheduler-widget-selected';
              } else if (isAvailable) {
                className += 'scheduler-widget-available';
              } else {
                className += 'scheduler-widget-not-available';
              }

              return (
                <button
                  key={`${timeSlot}-${formatDate(date)}`}
                  type="button"
                  class={className}
                  onClick={() => handleSlotClick(timeSlot, date)}
                  disabled={!isAvailable}
                  aria-pressed={isSelected ? 'true' : 'false'}
                  aria-label={slotLabel(timeSlot, date, isAvailable)}
                >
                  {(isAvailable || isSelected) && (
                    <div class="scheduler-widget-radio"></div>
                  )}
                </button>
              );
            })
          ])}
        </div>

        {/* Legend */}
        {showLegend && (
          <div class="scheduler-widget-legend">
            <div class="scheduler-widget-legend-item">
              <div class="scheduler-widget-legend-color scheduler-widget-available"></div>
              <span>{defaultText.availableTimeLabel}</span>
            </div>
            <div class="scheduler-widget-legend-item">
              <div class="scheduler-widget-legend-color scheduler-widget-not-available"></div>
              <span>{defaultText.notAvailableTimeLabel}</span>
            </div>
            <div class="scheduler-widget-legend-item">
              <div class="scheduler-widget-legend-color scheduler-widget-selected"></div>
              <span>{defaultText.selectedTimeLabel}</span>
            </div>
          </div>
        )}
      </div>

      {/* Selected slot info */}
      {selectedSlot && (
        <div class="scheduler-widget-selected-info" role="status">
          <strong>{defaultText.selectedAppointmentLabel}</strong>{' '}
          {selectedSlot.timeSlot} on{' '}
          {selectedSlot.date.toLocaleDateString('en-US', defaultText.dateOptions)}
        </div>
      )}
    </div>
  );
};

export default AppointmentScheduler;